"use client";

import React from "react";
import Slider from "react-slick";
import styled from "styled-components";

import {
  ArrowIcon,
  Text,
  TextColor,
  TextSize,
  TextVariant,
  TextWeight,
} from "../../components";
import { useMatchBreakpoints } from "../../hooks";
import type { Product } from "../../types/product.type";
import { ProductMiniature } from "../ProductMiniature";

type FeaturedProductsProps = {
  products: Product[];
  title?: string;
  smallTitle?: boolean;
  isLoading?: boolean;
  customTitle?: string;
  productView?: boolean;
};

// Skeleton component for loading state
const ProductSkeleton: React.FC = () => (
  <div className="animate-pulse px-1">
    <div className="bg-gray-200 mb-4 min-h-[500px]"></div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-6 bg-gray-200 rounded w-1/3"></div>
    </div>
  </div>
);

export const FeaturedProducts: React.FC<FeaturedProductsProps> = ({
  products,
  title = "Polecane dla Ciebie",
  smallTitle = false,
  isLoading = false,
  customTitle,
  productView = false,
}) => {
  const { isXs, isSm, isMd } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;

  const getSliderSettings = (productCount: number) => {
    return {
      dots: false,
      infinite: productCount > 4,
      speed: 500,
      slidesToShow: Math.min(4, productCount),
      slidesToScroll: 1,
      arrows: productCount > 4,
      autoplay: false,
      prevArrow: <CustomArrow direction="prev" />,
      nextArrow: <CustomArrow direction="next" />,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: Math.min(3, productCount),
            slidesToScroll: 1,
            infinite: productCount > 3,
            arrows: productCount > 3,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, productCount),
            slidesToScroll: 1,
            infinite: productCount > 2,
            arrows: productCount > 2,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: productCount > 1,
            arrows: productCount > 1,
          },
        },
      ],
    };
  };

  // Custom arrow component
  const CustomArrow = ({
    onClick,
    direction,
  }: {
    onClick?: () => void;
    direction: "prev" | "next";
  }) => (
    <button
      className={`slick-${direction}`}
      onClick={onClick}
      aria-label={`${direction === "prev" ? "Previous" : "Next"} slide`}
    >
      <ArrowIcon />
    </button>
  );

  const renderTitle = () => (
    <div
      className={`flex ${customTitle || smallTitle ? "justify-start" : "justify-center"}`}
    >
      {customTitle ? (
        <div className="w-full border-b border-gray-200 pb-2">
          <Text
            text={customTitle}
            weight={TextWeight.SEMIBOLD}
            color={TextColor.BLACK}
            fontSize={isMobile ? TextSize.S : TextSize.M}
          />
        </div>
      ) : (
        <Text
          text={title}
          weight={TextWeight.SEMIBOLD}
          textTransform={smallTitle ? undefined : "uppercase"}
          className={`${smallTitle ? "" : "tracking-widest"}`}
          color={TextColor.BLACK}
          fontSize={smallTitle || isMobile ? TextSize.S : TextSize.XS}
          variant={smallTitle || isMobile ? TextVariant.H5 : TextVariant.H4}
        />
      )}
    </div>
  );

  // Funkcja do renderowania produktów
  const renderProductsCarousel = (products: Product[]) => {
    const filteredProducts = products.filter(
      (product) =>
        product.combinations?.values && product.combinations?.values.length > 0,
    );
    const shouldUseCarousel = filteredProducts.length > 4 || isXs || isSm;
    const sliderSettings = getSliderSettings(filteredProducts.length);

    if (!shouldUseCarousel) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2 mt-10">
          {filteredProducts.map((product, index) => (
            <ProductMiniature
              key={`featured-product-miniature-${index}`}
              product={product as Product}
            />
          ))}
        </div>
      );
    }

    return (
      <ProductsCarouselWrapper productView={productView}>
        <div className="products-slider-container">
          <div className="products-slider">
            <Slider {...sliderSettings}>
              {filteredProducts.map((product, index) => (
                <div
                  key={`featured-product-miniature-${index}`}
                  className="product-slide"
                >
                  <ProductMiniature product={product as Product} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </ProductsCarouselWrapper>
    );
  };

  if (isLoading) {
    return (
      <div
        className={`w-full max-w-screen md:max-w-screen-2xl mx-auto ${
          customTitle ? "my-4" : "my-10"
        }`}
      >
        <div className="container-break">
          <div className="w-full max-w-screen flex flex-col justify-center">
            {renderTitle()}

            <ProductsCarouselWrapper productView={productView}>
              <div className="products-slider-container">
                <div className="products-slider">
                  <Slider {...getSliderSettings(4)}>
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div key={`featured-product-miniature-skeleton-${index}`}>
                        <ProductSkeleton />
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
            </ProductsCarouselWrapper>
          </div>
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div
      className={`w-full max-w-screen-2xl mx-auto ${
        customTitle ? "my-4" : "my-10"
      }`}
    >
      <div className="container-break">
        <div className="flex flex-col justify-center">
          {renderTitle()}

          {products &&
            products.length > 0 &&
            renderProductsCarousel(products as Product[])}
        </div>
      </div>
    </div>
  );
};

const ProductsCarouselWrapper = styled.div<{ productView?: boolean }>`
  width: 100%;
  ${({ productView }) =>
    productView &&
    `
      max-width: calc(100vw - 2rem - 14px);
    `}
  margin-top: 2.5rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: ${({ productView }) =>
      productView ? "calc(100vw - 2rem)" : "100vw"};
  }

  .products-slider-container {
    width: 100%;
    position: relative;

    .products-slider {
      height: auto;

      .product-slide {
        padding: 0 5px;
        height: 100%;

        > div {
          height: 100%;
        }
      }
    }
  }

  .slick-list {
    overflow: hidden;
    width: 100% !important;
  }

  .slick-track {
    display: flex;
    align-items: stretch;
  }

  .slick-slide {
    > div {
      height: 100%;
    }
  }

  .slick-next,
  .slick-prev {
    position: absolute;
    z-index: 1;
    width: 30px;
    height: 60px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    background: none;
    border: none;
    outline: none;
    display: block;
    fill: #000;

    svg {
      width: 30px;
      height: 60px;
      fill: #000;
    }

    &:before {
      display: none;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .slick-prev {
    left: 5px;
  }

  .slick-next {
    right: 5px;

    svg {
      transform: rotate(180deg);
    }
  }
`;

export default FeaturedProducts;
